import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { WardDietsView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import { wardDietsQuery } from '../api/kitchen.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { LoadFailed, NotAllowed, OfflineListNotice } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { DietDialog } from './diet-dialog.tsx';
import { nbmWords } from './diet-section.tsx';
import { RESTRICTED } from './words.tsx';

// A ward's diets (docs/plans/phase3-kitchen-and-housekeeping.md §1.4): each inpatient in the
// ward with the diet the kitchen serves them, and ordering or changing it from here. A stay
// the reader may not see is listed as a restricted record, without its diet.

const route = getRouteApi('/_app/wards/$wardId/diets');

type Patient = WardDietsView['patients'][number];

export function WardDietsPage() {
  const { wardId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'inpatient.read');
  const mayOrder = session !== null && can(session, 'diet.order');
  const diets = useQuery({ ...wardDietsQuery(wardId), enabled: allowed });
  const [ordering, setOrdering] = useState<Patient | null>(null);
  if (!allowed) return <NotAllowed title="Diets" what="see the ward's diets" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title={diets.data === undefined ? 'Diets' : `Diets, ${diets.data.ward.name}`}
        description="What the kitchen serves each patient in this ward."
        actions={
          <Link to="/wards/$wardId" params={{ wardId }}>
            Back to the ward
          </Link>
        }
      />
      <OfflineListNotice online={online} />
      <Section title="Patients">
        {diets.isPending ? <LoadingBlock label="Loading the diets" rows={4} /> : null}
        {diets.isError ? (
          <LoadFailed
            error={diets.error}
            fallback="The diets couldn't be loaded. Try again."
            onRetry={() => void diets.refetch()}
          />
        ) : null}
        {diets.isSuccess ? (
          <TableScroll>
            <Table flat aria-label="Diets in this ward">
              <TableHeader>
                <Column isRowHeader>Bed</Column>
                <Column>Patient</Column>
                <Column>Diet</Column>
                <Column>Nil by mouth</Column>
                <Column>Order</Column>
              </TableHeader>
              <TableBody
                items={diets.data.patients.map((p) => ({ ...p, id: p.admissionId }))}
                renderEmptyState={() => 'Nobody is admitted to this ward.'}
              >
                {(p) => (
                  <Row id={p.id}>
                    <Cell>{p.bedLabel === null ? 'No bed yet' : `Bed ${p.bedLabel}`}</Cell>
                    <Cell>
                      {p.patient === null ? (
                        RESTRICTED
                      ) : (
                        <Link to="/admissions/$admissionId" params={{ admissionId: p.id }}>
                          {p.patient.displayName}
                        </Link>
                      )}
                    </Cell>
                    <Cell>{p.current?.words ?? 'No diet ordered'}</Cell>
                    <Cell>{p.current === null ? '' : (nbmWords(p.current) ?? 'No')}</Cell>
                    <Cell>
                      {mayOrder && p.patient !== null ? (
                        <Button
                          dense
                          isDisabled={!online}
                          aria-label={`${p.current === null ? 'Order diet' : 'Change diet'} for ${p.patient.displayName}`}
                          onPress={() => {
                            setOrdering(p);
                          }}
                        >
                          {p.current === null ? 'Order diet' : 'Change diet'}
                        </Button>
                      ) : null}
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
      </Section>
      {ordering === null ? null : (
        <DietDialog
          admissionId={ordering.admissionId}
          current={ordering.current}
          onClose={() => {
            setOrdering(null);
          }}
        />
      )}
    </div>
  );
}
